import { Check, Lock } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { useSetAIConsent, useSettings } from '../api/hooks';
import { useUI } from '../lib/ui';
import { GeminiPrivacy } from './GeminiPrivacy';

export const CONSENT_LABEL = "I understand what's sent to Gemini";

/** The "I understand what's sent to Gemini" box: a real checkbox, drawn like the app's others. */
export function ConsentCheckbox({ checked, onChange, describedBy }: { checked: boolean; onChange: (checked: boolean) => void; describedBy?: string }) {
  return (
    <label className="consent">
      <input type="checkbox" className="sr-only" checked={checked} onChange={(e) => onChange(e.target.checked)} aria-describedby={describedBy} />
      <span className={`check ${checked ? 'is-on' : ''}`} aria-hidden="true">
        <Check size={15} />
      </span>
      <span className="small" style={{ fontWeight: 600 }}>
        {CONSENT_LABEL}
      </span>
    </label>
  );
}

/**
 * Next to an AI feature while AI is off until the person agrees to what's sent to Gemini (sign-in
 * on): a line saying so, and the full privacy text with the box to tick, opened in place so they
 * can agree without leaving what they were doing. Agreeing turns the feature on where it stands,
 * and `onAgreed` lets the parent say so in its place (it moves focus there).
 */
export function AIConsentPrompt({ id, onAgreed }: { id?: string; onAgreed: () => void }) {
  const { toast } = useUI();
  const paidTier = useSettings().data?.ai.paidTier ?? false;
  const consent = useSetAIConsent();
  const [open, setOpen] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const reviewRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // The button that opened it goes away: move focus to the text it opened.
  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  const close = () => {
    setOpen(false);
    requestAnimationFrame(() => reviewRef.current?.focus());
  };

  return (
    <div id={id} className="consent-prompt">
      <p className="consent-prompt__line">
        <Lock size={16} aria-hidden="true" />
        <span>AI is off for your account until you agree to what's sent to Gemini.</span>
      </p>
      {open ? (
        <div ref={panelRef} tabIndex={-1} role="group" aria-label="What's sent to Gemini" className="consent-prompt__panel stack" style={{ gap: 12 }}>
          <GeminiPrivacy paidTier={paidTier} />
          <ConsentCheckbox checked={agreed} onChange={setAgreed} />
          <div className="row wrap">
            <button
              type="button"
              className="btn btn--primary btn--sm"
              disabled={!agreed || consent.isPending}
              onClick={() => consent.mutate(true, { onSuccess: onAgreed, onError: (e) => toast(e.message, { tone: 'error' }) })}
            >
              Turn on AI
            </button>
            <button type="button" className="btn btn--quiet btn--sm" onClick={close}>
              Not now
            </button>
          </div>
        </div>
      ) : (
        <button ref={reviewRef} type="button" className="btn btn--ghost btn--sm" onClick={() => setOpen(true)}>
          Read it and turn on AI
        </button>
      )}
    </div>
  );
}

/**
 * You's AI section, with sign-in on: tick the box under the privacy text to turn AI on, or turn it
 * off again. Shown only while AI is on for the server and the account (or waiting for this).
 */
export function AIConsentControls({ consentAt, needsConsent }: { consentAt: string | null; needsConsent: boolean }) {
  const { toast } = useUI();
  const consent = useSetAIConsent();
  const [agreed, setAgreed] = useState(false);
  const noteId = useId();

  if (needsConsent) {
    return (
      <div className="stack" style={{ gap: 10 }}>
        <ConsentCheckbox checked={agreed} onChange={setAgreed} describedBy={noteId} />
        <p id={noteId} className="tiny muted">
          AI stays off for your account until you tick this. Nothing of yours is sent to Gemini before then.
        </p>
        <button
          type="button"
          className="btn btn--primary btn--sm"
          style={{ alignSelf: 'flex-start' }}
          disabled={!agreed || consent.isPending}
          onClick={() =>
            consent.mutate(true, {
              onSuccess: () => toast('AI is on for your account'),
              onError: (e) => toast(e.message, { tone: 'error' }),
            })
          }
        >
          Turn on AI
        </button>
      </div>
    );
  }
  const day = consentAt && /^\d{4}-\d{2}-\d{2}/.test(consentAt) ? new Date(`${consentAt.slice(0, 10)}T12:00:00`) : null;
  return (
    <div className="row between wrap" style={{ gap: 10 }}>
      <p className="small muted">
        {day ? `You agreed to this on ${day.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}.` : 'You agreed to this.'}
      </p>
      <button
        type="button"
        className="btn btn--quiet btn--sm"
        disabled={consent.isPending}
        onClick={() =>
          consent.mutate(false, {
            onSuccess: () => toast('AI is off for your account. Turn it back on here any time.'),
            onError: (e) => toast(e.message, { tone: 'error' }),
          })
        }
      >
        Turn off AI for my account
      </button>
    </div>
  );
}
